import { Injectable, inject } from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';

import {TimeService, ArraySort} from '@shared';

import {
    TaskFeasibility,
    ProblemInstanceService,
    FeasibilityService,
    PatientService,
    BaseTask,
    SkillRequirement,
    TaskToRequiredEmployees,
    TaskIdInfo
} from 'planner-shared';

import {RouteTaskView} from '../../route-task-view.model';
import {RosterService} from "../../../../shared/services/roster/roster.service";

@Injectable({
    providedIn: 'root'
})
export class UnallocatedTaskService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly feasibilityService = inject(FeasibilityService);
    private readonly patientService = inject(PatientService);
    private readonly timeService = inject(TimeService);
    private readonly rosterService = inject(RosterService);

    private taskFeasibility: TaskFeasibility;

    public getUnallocatedTasks(unallocatedTasks: TaskIdInfo[]): Observable<RouteTaskView[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result => {
                if (this.taskFeasibility) {
                    return of(result.problemInstance);
                }

                const synchronizedTaskIds: string[] = [];
                result.problemInstance.syncedTaskPairs.forEach(syncedPair => {
                    synchronizedTaskIds.push(syncedPair.syncedMasterTaskId);
                    synchronizedTaskIds.push(syncedPair.syncedWithTaskId);
                });
                result.problemInstance.tasks.filter(task => task.numberOfEmployees > 1).forEach(
                    task => synchronizedTaskIds.push(task.taskId)
                );

                const unallocatedTaskIds = [];
                unallocatedTasks.forEach(unallocatedTask => {
                    unallocatedTaskIds.push(unallocatedTask.taskId);
                    if (!synchronizedTaskIds.includes(unallocatedTask.taskId)) {
                        unallocatedTaskIds.push(unallocatedTask.routePlannerTaskId);
                    }
                });
                return this.feasibilityService.getFeasibility(unallocatedTaskIds).pipe(
                    tap(feasibility => (this.taskFeasibility = feasibility.taskFeasibility)),
                    map(() => result.problemInstance)
                );
            }),
            switchMap(problemInstance =>
                this.patientService.getPatients().pipe(
                    map(patients => {
                        const tasks = unallocatedTasks
                            .map(taskIdInfo => {
                                const patientTask =
                                    problemInstance.tasks.find(i => i.originalTaskId === taskIdInfo.taskId);
                                if (patientTask) {
                                    const patient = patients.find(i => i.patientId === patientTask.patientId);
                                    const patientName = patient == null ? '' : patient.patientName;
                                    const taskDisabled = patient && !patient.addressValid;
                                    const routeTaskView = this.mapTask(
                                        patientTask,
                                        problemInstance.skillLevels,
                                        patientName,
                                        patientTask.patientId,
                                        taskDisabled,
                                        problemInstance.taskToReqEmployees,
                                        patientTask.isSyncedInitially || patientTask.numberOfEmployees > 1
                                    );

                                    routeTaskView.serviceName = patientTask.taskDescription;
                                    routeTaskView.routePlannerTaskId = taskIdInfo.routePlannerTaskId;
                                    routeTaskView.isEmployeeTask = false;
                                    routeTaskView.shiftType = patientTask.shiftType;
                                    if (routeTaskView.routePlannerTaskId) {
                                        routeTaskView.rosterDepartmentId = patientTask.rosterDepartmentId;
                                        routeTaskView.rosterDepartmentName
                                            = this.rosterService.getRosterNameById(patientTask.rosterDepartmentId);
                                    }
                                    return routeTaskView;
                                }

                                const employeeTask = problemInstance.emTasksNotPatients.find(
                                    i => i.taskId === taskIdInfo.routePlannerTaskId
                                );

                                if (employeeTask) {
                                    const employee = problemInstance.employeeWorkShifts.find(
                                        i => i.workHourId === employeeTask.workHourId
                                    );

                                    const employeeName = employee ? employee.employeeName : '';
                                    const routeTask = this.mapTask(
                                        employeeTask,
                                        problemInstance.skillLevels,
                                        employeeName
                                    );
                                    if (routeTask.rosterDepartmentId) {
                                        routeTask.rosterDepartmentId = patientTask.rosterDepartmentId;
                                        routeTask.rosterDepartmentName
                                            = this.rosterService.getRosterNameById(patientTask.rosterDepartmentId);
                                    }
                                    routeTask.shiftType = employeeTask.shiftType;
                                    routeTask.isEmployeeTask = true;
                                    routeTask.routePlannerTaskId = employeeTask.taskId;
                                    routeTask.taskId = employeeTask.originalTaskId;
                                    routeTask.originalTaskId = employeeTask.originalTaskId;
                                    routeTask.isShiftSplitTask = employeeTask.isShiftSplitTask;
                                    return routeTask;
                                }

                                const generalTask = problemInstance.geTasksNotPatients.find(
                                    i => i.originalTaskId === taskIdInfo.taskId
                                );

                                if (generalTask) {
                                    const routeTask = this.mapTask(generalTask, problemInstance.skillLevels);
                                    routeTask.allowedShiftCodes = generalTask.allowedShiftCodes;
                                    routeTask.prohibitedShiftCodes = generalTask.prohibitedShiftCodes;
                                    routeTask.routePlannerTaskId = taskIdInfo.routePlannerTaskId;
                                    routeTask.taskId = taskIdInfo.taskId;
                                    routeTask.isEmployeeTask = true;
                                    routeTask.streetAddressNameNumberAndLetter
                                        = generalTask.streetAddressNameNumberAndLetter;
                                    if (routeTask.rosterDepartmentId) {
                                        routeTask.rosterDepartmentId = generalTask.rosterDepartmentId;
                                        routeTask.rosterDepartmentName
                                            = this.rosterService.getRosterNameById(generalTask.rosterDepartmentId);
                                    }

                                    return routeTask;
                                }

                                return null;
                            })
                            .filter(task => task !== null);

                        ArraySort.sort(tasks, 'fromTime', true, 'toTime', true);
                        tasks.sort((a, b) => a.feasibilityInfo.feasibilityCount - b.feasibilityInfo.feasibilityCount);

                        return tasks;
                    })
                )
            ),
        );
    }

    public clearTaskFeasibility(): void {
        this.taskFeasibility = null;
    }

    private mapTask(
        task: BaseTask,
        skillLevels: SkillRequirement[],
        name?: string,
        patientId?: string,
        disabled?: boolean,
        taskToReqEmployees?: TaskToRequiredEmployees[],
        isSynced?: boolean
    ): RouteTaskView {
        const skillLevel = skillLevels.find(i => i.skillLevelId === task.skillRequirement);
        const requiredEmployees = taskToReqEmployees?.find(i => i.taskId === task.originalTaskId);
        const routeTask = <RouteTaskView>{
            taskId: task.taskId,
            originalTaskId: task.originalTaskId,
            patientId,
            name,
            serviceName: task.serviceName,
            duration: task.duration,
            skillLevelDescription: skillLevel == null ? '' : skillLevel.skillLevelDescription,
            fromTime: this.timeService.convertTime(task.fromTime, true),
            toTime: this.timeService.convertTime(task.toTime, true),
            skillRequirement: task.skillRequirement,
            numberOfHigherSkillLevelsAccepted: task.numberOfHigherSkillLevelsAccepted,
            requiredEmployees: requiredEmployees?.employeeIds,
            disabledPermanently: disabled,
            isSynced,
            isHeavy: task.isHeavy,
            doesRequirePhysicalAppearance: task.doesRequirePhysicalAppearance,
            streetAddressNameNumberAndLetter: task.streetAddressNameNumberAndLetter,
            shiftType: null
        };

        routeTask.feasibilityInfo = this.feasibilityService.getTaskFeasibilityInfo(
            routeTask.taskId,
            this.taskFeasibility,
            true
        );

        if (routeTask.feasibilityInfo.feasibilityCount > 0) {
            routeTask.disabledPermanently = true;
        }

        return routeTask;
    }
}
